import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Bell, 
  RefreshCw, 
  Play, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Smartphone,
  Send,
  Timer
} from 'lucide-react';
import { useNotificationQueue } from '@/hooks/useNotificationQueue';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

export const NotificationQueueMonitor: React.FC = () => {
  const {
    queueStats,
    notificationHistory,
    pushConfig,
    isLoading,
    triggerQueueProcessing,
    refetchStats,
    refetchConfig
  } = useNotificationQueue(true); // Use admin mode

  const [isProcessing, setIsProcessing] = useState(false);

  const handleTriggerProcessing = async () => {
    setIsProcessing(true);
    try {
      const success = await triggerQueueProcessing();
      if (success) {
        toast.success('Queue processing triggered successfully');
      } else {
        toast.error('Failed to trigger queue processing');
      }
    } catch (error) {
      console.error('Error triggering queue processing:', error);
      toast.error('Failed to trigger queue processing');
    } finally {
      setIsProcessing(false);
    }
  };

  const getQueueHealthColor = () => {
    if (!pushConfig) return 'text-muted-foreground';
    
    switch (pushConfig.queueHealth) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  const getQueueHealthIcon = () => {
    if (!pushConfig) return <Clock className="h-4 w-4" />;
    
    switch (pushConfig.queueHealth) {
      case 'healthy': return <CheckCircle className="h-4 w-4" />;
      case 'warning': return <AlertCircle className="h-4 w-4" />;
      case 'error': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getNotificationStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Sent</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'processing':
        return <Badge variant="secondary"><Timer className="h-3 w-3 mr-1" />Processing</Badge>;
      case 'pending':
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Queue Monitor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            <span>Loading notification queue data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Queue Status Overview */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <CardTitle>Notification Queue Status</CardTitle>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  refetchStats();
                  refetchConfig();
                }}
                disabled={isLoading}
                className="w-full sm:w-auto"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''} sm:mr-0`} />
                <span className="ml-2 sm:hidden">Refresh</span>
              </Button>
              <Button 
                variant="default" 
                size="sm" 
                onClick={handleTriggerProcessing}
                disabled={isProcessing || isLoading}
                className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
              >
                {isProcessing ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                <span className="hidden sm:inline">Manual Trigger</span>
                <span className="sm:hidden">Trigger</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">
                {queueStats?.pending || 0}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Processing</div>
              <div className="text-2xl font-bold text-blue-600">
                {queueStats?.processing || 0}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Sent</div>
              <div className="text-2xl font-bold text-green-600">
                {queueStats?.sent || 0}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Failed</div>
              <div className="text-2xl font-bold text-red-600">
                {queueStats?.failed || 0}
              </div>
            </div>
          </div>

          {/* Push Configuration Status */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
            <h4 className="font-medium flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Push Configuration
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div className="flex items-center justify-between p-2 rounded bg-background/50">
                <span className="text-muted-foreground">Active Tokens:</span>
                <Badge variant="outline">
                  {pushConfig?.tokenCount !== undefined ? pushConfig.tokenCount : '-'}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-background/50">
                <span className="text-muted-foreground">Platform:</span>
                <Badge variant="secondary">{pushConfig?.platform || 'Unknown'}</Badge>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-background/50 sm:col-span-2 lg:col-span-1">
                <span className="text-muted-foreground">Queue Health:</span>
                <div className={`flex items-center gap-1 ${getQueueHealthColor()}`}>
                  {getQueueHealthIcon()}
                  <span className="capitalize">{pushConfig?.queueHealth || 'Unknown'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {queueStats?.pending > 50 && (
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                High number of pending notifications ({queueStats.pending}). Consider triggering manual processing.
              </AlertDescription>
            </Alert>
          )}

          {!pushConfig?.hasActiveTokens && (
            <Alert className="border-orange-200 bg-orange-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No active push tokens found. Users need to enable push notifications in the app.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Recent Notification History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Recent Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notificationHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No recent notifications found
            </div>
          ) : (
            <div className="space-y-3">
              {/* Desktop table header */}
              <div className="hidden md:grid grid-cols-5 gap-4 pb-2 border-b text-sm font-medium text-muted-foreground">
                <div>Type</div>
                <div>Entity</div>
                <div>Status</div>
                <div>Retries</div>
                <div>Created</div>
              </div>
              
              {notificationHistory.slice(0, 10).map((notification) => (
                <div key={notification.id}>
                  {/* Desktop table row */}
                  <div className="hidden md:grid grid-cols-5 gap-4 py-2 text-sm border-b border-muted/30 hover:bg-muted/20 rounded">
                    <div>
                      <Badge variant="outline" className="text-xs">
                        {notification.notification_type}
                      </Badge>
                    </div>
                    <div className="truncate" title={notification.entity_name || 'N/A'}>
                      {notification.entity_name || 'N/A'}
                    </div>
                    <div>
                      {getNotificationStatusBadge(notification.status)}
                    </div>
                    <div className="text-center">
                      <Badge variant={notification.retry_count > 0 ? "destructive" : "secondary"}>
                        {notification.retry_count || 0}/{notification.max_retries || 3}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </div>
                  </div>
                  
                  {/* Mobile card layout */}
                  <div className="md:hidden p-3 border rounded-lg space-y-2 hover:bg-muted/20">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs">
                        {notification.notification_type}
                      </Badge>
                      {getNotificationStatusBadge(notification.status)}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Entity: </span>
                      <span className="text-muted-foreground">{notification.entity_name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <span>Retries: </span>
                        <Badge variant={notification.retry_count > 0 ? "destructive" : "secondary"} className="text-xs">
                          {notification.retry_count || 0}/{notification.max_retries || 3}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};